import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import SalviaLayout from "@/components/SalviaLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, ClipboardList, Loader2, Plus, RefreshCw, Search } from "lucide-react";
import type {
  ClientRegistrationActivationChecklist,
  ClientRegistrationAdminStatus,
  ClientRegistrationChannelStatus,
} from "../../../shared/clientRegistrationRequests";
import {
  CLIENT_REGISTRATION_ADMIN_STATUS_OPTIONS,
  CLIENT_REGISTRATION_CHANNEL_STATUS_OPTIONS,
} from "../../../shared/clientRegistrationRequests";

const CHANNEL_ROLES = ["channel_admin", "channel_admin_multi", "channel_operator", "channel_consultant"] as const;

type CreateFormState = {
  nomeFantasia: string;
  razaoSocial: string;
  cnpj: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
  cnae: string;
  grauRisco: string;
  atividadePrincipal: string;
  sector: string;
  employeesCount: string;
  email: string;
  phone: string;
  responsibleName: string;
  contactRole: string;
  sellerName: string;
  sellerEmail: string;
  sellerPhone: string;
  requestedServices: string[];
  contractContext: string;
  notes: string;
  contractStartDate: string;
  contractEndDate: string;
  contractAttachmentName: string;
};

type ChannelServiceOption = {
  id: number;
  value: string;
  key: string;
  label: string;
  description?: string | null;
  category?: string | null;
};

function createEmptyForm(): CreateFormState {
  return {
    nomeFantasia: "",
    razaoSocial: "",
    cnpj: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    cep: "",
    cnae: "",
    grauRisco: "",
    atividadePrincipal: "",
    sector: "",
    employeesCount: "",
    email: "",
    phone: "",
    responsibleName: "",
    contactRole: "",
    sellerName: "",
    sellerEmail: "",
    sellerPhone: "",
    requestedServices: [],
    contractContext: "",
    notes: "",
    contractStartDate: "",
    contractEndDate: "",
    contractAttachmentName: "",
  };
}

function createEmptyChecklist(): ClientRegistrationActivationChecklist {
  return {
    clientConfigured: false,
    contractConfigured: false,
    servicesConfigured: false,
    integrationsConfigured: false,
    finalValidationCompleted: false,
  };
}

function cleanDigits(value: string) {
  return value.replace(/\D/g, "");
}

function maskCnpj(value: string) {
  const digits = cleanDigits(value).slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}

function maskCep(value: string) {
  const digits = cleanDigits(value).slice(0, 8);
  return digits.replace(/^(\d{5})(\d)/, "$1-$2");
}

function maskPhone(value: string) {
  const digits = cleanDigits(value).slice(0, 11);
  if (digits.length <= 10) {
    return digits.replace(/^(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
  }
  return digits.replace(/^(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
}

function extractContractDateFromContext(context: unknown, label: "Início previsto do contrato" | "Encerramento previsto do contrato") {
  if (typeof context !== "string" || !context.trim()) {
    return "";
  }

  const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = context.match(new RegExp(`${escapedLabel}:\\s*(\\d{4}-\\d{2}-\\d{2})`));
  return match?.[1] ?? "";
}

export default function ClientRegistrationRequests() {
  const { user } = useAuth();
  const structuralRole = typeof (user as any)?.role === "string" ? ((user as any).role as string) : "";
  const canAdminister = ["admin", "salvia_operation_admin"].includes(structuralRole);
  const showAdminChannelColumn = canAdminister;
  const canCreate = ["channel_admin", "channel_admin_multi", "channel_operator"].includes(structuralRole);
  const [search, setSearch] = useState("");
  const [channelStatus, setChannelStatus] = useState<string>("all");
  const [adminStatus, setAdminStatus] = useState<string>("all");
  const [selectedChannelId, setSelectedChannelId] = useState<string>("all");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [pendingDialogOpen, setPendingDialogOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null);
  const [createForm, setCreateForm] = useState<CreateFormState>(() => createEmptyForm());
  const [pendingTitle, setPendingTitle] = useState("");
  const [pendingMessage, setPendingMessage] = useState("");
  const [pendingResponse, setPendingResponse] = useState("");
  const [activationSummary, setActivationSummary] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [checklistNote, setChecklistNote] = useState("");
  const [checklist, setChecklist] = useState<ClientRegistrationActivationChecklist>(() => createEmptyChecklist());

  const listQuery = trpc.clientRegistrationRequests.list.useQuery(
    {
      search: search.trim() || undefined,
      ...(canAdminister && adminStatus !== "all" ? { adminStatus: adminStatus as ClientRegistrationAdminStatus } : {}),
      ...(canAdminister && selectedChannelId !== "all" ? { channelId: Number(selectedChannelId) } : {}),
      ...(!canAdminister && channelStatus !== "all" ? { channelStatus: channelStatus as ClientRegistrationChannelStatus } : {}),
    } as any,
    { staleTime: 0 },
  );

  const channelsQuery = trpc.channels.list.useQuery(undefined, {
    enabled: canAdminister,
    staleTime: 60_000,
  });

  const serviceOptionsQuery = trpc.clientRegistrationRequests.getServiceOptions.useQuery(undefined, {
    staleTime: Infinity,
    enabled: canCreate,
  });

  const detailQuery = trpc.clientRegistrationRequests.getById.useQuery(
    { id: selectedRequestId ?? 0 },
    { enabled: Boolean(selectedRequestId && detailDialogOpen), staleTime: 0 },
  );

  const utils = trpc.useUtils();
  const refreshRequests = async () => {
    await utils.clientRegistrationRequests.list.invalidate();
    if (selectedRequestId) {
      await utils.clientRegistrationRequests.getById.invalidate({ id: selectedRequestId });
    }
  };

  const createMutation = trpc.clientRegistrationRequests.create.useMutation({
    onSuccess: async (data) => {
      toast.success(`Solicitação ${data.protocol} registrada com sucesso.`);
      setCreateDialogOpen(false);
      setCreateForm(createEmptyForm());
      await refreshRequests();
    },
    onError: (error) => {
      toast.error(error.message || "Não foi possível registrar a solicitação.");
    },
  });

  const requestComplementMutation = trpc.clientRegistrationRequests.requestComplement.useMutation({
    onSuccess: async () => {
      toast.success("Pendência registrada para o Canal.");
      setPendingDialogOpen(false);
      setPendingTitle("");
      setPendingMessage("");
      await refreshRequests();
      await detailQuery.refetch();
    },
    onError: (error) => toast.error(error.message || "Não foi possível registrar a pendência."),
  });

  const respondComplementMutation = trpc.clientRegistrationRequests.respondComplement.useMutation({
    onSuccess: async () => {
      toast.success("Resposta enviada para análise administrativa.");
      setPendingResponse("");
      await refreshRequests();
      await detailQuery.refetch();
    },
    onError: (error) => toast.error(error.message || "Não foi possível responder a pendência."),
  });

  const approveMutation = trpc.clientRegistrationRequests.approve.useMutation({
    onSuccess: async () => {
      toast.success("Solicitação aprovada para implantação.");
      await refreshRequests();
      await detailQuery.refetch();
    },
    onError: (error) => toast.error(error.message || "Não foi possível aprovar a solicitação."),
  });

  const startParameterizationMutation = trpc.clientRegistrationRequests.startParameterization.useMutation({
    onSuccess: async () => {
      toast.success("Parametrização iniciada com sucesso.");
      await refreshRequests();
      await detailQuery.refetch();
    },
    onError: (error) => toast.error(error.message || "Não foi possível iniciar a parametrização."),
  });

  const saveChecklistMutation = trpc.clientRegistrationRequests.saveChecklist.useMutation({
    onSuccess: async (data) => {
      toast.success(
        data.status === "READY_FOR_ACTIVATION"
          ? "Checklist concluído. Solicitação pronta para ativação."
          : "Checklist atualizado com sucesso.",
      );
      await refreshRequests();
      await detailQuery.refetch();
    },
    onError: (error) => toast.error(error.message || "Não foi possível atualizar o checklist."),
  });

  const activateMutation = trpc.clientRegistrationRequests.activate.useMutation({
    onSuccess: async () => {
      toast.success("Solicitação encerrada com sucesso. O Canal agora visualiza o status Apto para operar.");
      setActivationSummary("");
      await refreshRequests();
      await detailQuery.refetch();
    },
    onError: (error) => toast.error(error.message || "Não foi possível concluir a solicitação."),
  });

  const rejectMutation = trpc.clientRegistrationRequests.reject.useMutation({
    onSuccess: async () => {
      toast.success("Solicitação reprovada.");
      setRejectionReason("");
      await refreshRequests();
      await detailQuery.refetch();
    },
    onError: (error) => toast.error(error.message || "Não foi possível reprovar a solicitação."),
  });

  const reprocessDeskMutation = trpc.clientRegistrationRequests.reprocessDeskManager.useMutation({
    onSuccess: async () => {
      toast.success("Integração com Desk Manager reprocessada.");
      await refreshRequests();
      await detailQuery.refetch();
    },
    onError: (error) => toast.error(error.message || "Não foi possível reprocessar a integração."),
  });

  const requests = listQuery.data?.items ?? [];
  const selectedRequest = detailQuery.data?.request as any;
  const permissions = detailQuery.data?.permissions as any;
  const events = (detailQuery.data?.events as any[]) ?? [];
  const requestPayload = (selectedRequest?.payload as any) ?? {};
  const selectedAdminStatus = (selectedRequest?.adminStatus as ClientRegistrationAdminStatus | undefined) ?? undefined;
  const isTerminalRequest = ["ACTIVATED", "REJECTED", "CANCELLED"].includes(selectedAdminStatus ?? "");
  const canEditImplementation = canAdminister && ["IN_PARAMETERIZATION", "READY_FOR_ACTIVATION"].includes(selectedAdminStatus ?? "");
  const showImplementationPanel = canAdminister && ["IN_PARAMETERIZATION", "READY_FOR_ACTIVATION", "ACTIVATED", "REJECTED", "CANCELLED"].includes(selectedAdminStatus ?? "");
  const canRejectRequest = canAdminister && !isTerminalRequest;
  const resolvedContractStartDate = requestPayload.contractStartDate || extractContractDateFromContext(selectedRequest?.contractContext, "Início previsto do contrato");
  const resolvedContractEndDate = requestPayload.contractEndDate || extractContractDateFromContext(selectedRequest?.contractContext, "Encerramento previsto do contrato");
  const requestedServiceDetails = Array.isArray(requestPayload?.requestedServicesDetailed) ? requestPayload.requestedServicesDetailed : [];
  const serviceOptions = (serviceOptionsQuery.data ?? []) as ChannelServiceOption[];

  const overviewCards = useMemo(() => {
    const total = requests.length;
    const pending = requests.filter((item: any) => item.channelStatus === "PENDENTE_COMPLEMENTACAO").length;
    const inFlight = requests.filter((item: any) => ["EM_ANALISE", "APROVADA_PARA_IMPLANTACAO", "EM_IMPLANTACAO"].includes(item.channelStatus)).length;
    const ready = requests.filter((item: any) => item.channelStatus === "APTO_PARA_OPERAR").length;
    return { total, pending, inFlight, ready };
  }, [requests]);

  const handleOpenDetails = (requestId: number) => {
    setSelectedRequestId(requestId);
    setDetailDialogOpen(true);
  };

  const handleToggleService = (service: string, checked: boolean) => {
    setCreateForm((prev) => ({
      ...prev,
      requestedServices: checked
        ? Array.from(new Set([...prev.requestedServices, service]))
        : prev.requestedServices.filter((item) => item !== service),
    }));
  };

  const hasValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.email.trim());
  const hasValidPhone = cleanDigits(createForm.phone).length >= 10;
  const hasValidSellerEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.sellerEmail.trim());
  const hasValidCnpj = cleanDigits(createForm.cnpj).length === 14;
  const hasRequiredCompanyData = Boolean(createForm.nomeFantasia.trim() && createForm.razaoSocial.trim() && hasValidCnpj);
  const hasRequiredContactData = Boolean(createForm.responsibleName.trim() && hasValidEmail && hasValidPhone);
  const hasRequiredSellerData = Boolean(createForm.sellerName.trim() && hasValidSellerEmail);
  const hasRequestedServices = createForm.requestedServices.length > 0;
  const hasRequiredContractData = Boolean(createForm.employeesCount.trim() && createForm.contractStartDate && createForm.contractEndDate && hasRequestedServices);
  const isCreateFormValid = hasRequiredCompanyData && hasRequiredContactData && hasRequiredSellerData && hasRequiredContractData;

  const handleSubmitCreate = async () => {
    if (!hasRequiredCompanyData) {
      toast.error("Preencha nome fantasia, razão social e um CNPJ válido para continuar.");
      return;
    }

    if (!hasRequiredContactData) {
      toast.error("Informe um ponto focal com nome, e-mail corporativo e telefone válidos.");
      return;
    }

    if (!hasRequiredSellerData) {
      toast.error("Informe os dados do vendedor com nome e e-mail válidos antes de enviar.");
      return;
    }

    if (!hasRequiredContractData) {
      toast.error("Preencha volume de vidas, período previsto do contrato e selecione ao menos um serviço antes de enviar.");
      return;
    }

    const contractContextParts = [
      createForm.contractStartDate ? `Início previsto do contrato: ${createForm.contractStartDate}` : null,
      createForm.contractEndDate ? `Encerramento previsto do contrato: ${createForm.contractEndDate}` : null,
      createForm.contractContext.trim() ? createForm.contractContext.trim() : null,
    ].filter(Boolean);

    const notesParts = [
      createForm.notes.trim() ? createForm.notes.trim() : null,
      createForm.contractAttachmentName ? `Arquivo de contrato informado pelo canal: ${createForm.contractAttachmentName}` : null,
    ].filter(Boolean);

    await createMutation.mutateAsync({
      ...createForm,
      cnpj: cleanDigits(createForm.cnpj),
      logradouro: createForm.logradouro.trim() || undefined,
      numero: createForm.numero.trim() || undefined,
      bairro: createForm.bairro.trim() || undefined,
      cidade: createForm.cidade.trim() || undefined,
      estado: createForm.estado.trim() || undefined,
      cep: cleanDigits(createForm.cep) || undefined,
      phone: cleanDigits(createForm.phone),
      employeesCount: createForm.employeesCount ? Number(createForm.employeesCount) : null,
      complemento: createForm.complemento || null,
      cnae: createForm.cnae || null,
      grauRisco: createForm.grauRisco || null,
      atividadePrincipal: createForm.atividadePrincipal || null,
      sector: createForm.sector || null,
      email: createForm.email || null,
      contactRole: createForm.contactRole || null,
      sellerName: createForm.sellerName.trim(),
      sellerEmail: createForm.sellerEmail.trim(),
      sellerPhone: cleanDigits(createForm.sellerPhone) || null,
      contractContext: contractContextParts.length ? contractContextParts.join("\n") : null,
      notes: notesParts.length ? notesParts.join("\n\n") : null,
    } as any);
  };

  const handleChecklistChange = (key: keyof ClientRegistrationActivationChecklist, checked: boolean) => {
    setChecklist((prev) => ({ ...prev, [key]: checked }));
  };

  const syncChecklistFromRequest = () => {
    if (!selectedRequest?.checklist) {
      setChecklist(createEmptyChecklist());
      setChecklistNote("");
      setActivationSummary("");
      return;
    }
    setChecklist(selectedRequest.checklist);
    setChecklistNote(selectedRequest.adminNotes ?? "");
    setActivationSummary(selectedRequest.finalOutcomeSummary ?? "");
  };

  useEffect(() => {
    if (!detailDialogOpen || !selectedRequest) {
      return;
    }

    setChecklist(selectedRequest.checklist ?? createEmptyChecklist());
    setChecklistNote(selectedRequest.adminNotes ?? "");
    setActivationSummary(selectedRequest.finalOutcomeSummary ?? "");
  }, [
    detailDialogOpen,
    selectedRequestId,
    selectedRequest?.adminNotes,
    selectedRequest?.finalOutcomeSummary,
    selectedRequest?.clientConfigured,
    selectedRequest?.contractConfigured,
    selectedRequest?.servicesConfigured,
    selectedRequest?.integrationsConfigured,
    selectedRequest?.finalValidationCompleted,
  ]);

  const title = canAdminister ? "Solicitações de Cadastro" : "Solicitações de cadastro";
  const subtitle = canAdminister
    ? "Fila administrativa para triagem, aprovação, parametrização e ativação de novos clientes."
    : "Acompanhe os cadastros solicitados pelo seu canal antes de entrarem na carteira ativa.";

  return (
    <SalviaLayout title={title}>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{title}</h1>
            <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => listQuery.refetch()} disabled={listQuery.isFetching}>
              <RefreshCw className={`mr-2 h-4 w-4 ${listQuery.isFetching ? "animate-spin" : ""}`} />
              Atualizar
            </Button>
            {canCreate && (
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Solicitação de novo cliente
              </Button>
            )}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Total</p>
            <p className="mt-3 text-3xl font-semibold text-slate-900">{overviewCards.total}</p>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">Pendências</p>
            <p className="mt-3 text-3xl font-semibold text-amber-900">{overviewCards.pending}</p>
          </div>
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-700">Em andamento</p>
            <p className="mt-3 text-3xl font-semibold text-blue-900">{overviewCards.inFlight}</p>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">Aptos para operar</p>
            <p className="mt-3 text-3xl font-semibold text-emerald-900">{overviewCards.ready}</p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1.4fr)_220px_220px_220px]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por protocolo, canal, razão social, nome fantasia ou CNPJ" className="pl-9" />
            </div>
            {canAdminister ? (
              <Select value={adminStatus} onValueChange={setAdminStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os status internos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status internos</SelectItem>
                  {CLIENT_REGISTRATION_ADMIN_STATUS_OPTIONS.map((option: { value: string; label: string }) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Select value={channelStatus} onValueChange={setChannelStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  {CLIENT_REGISTRATION_CHANNEL_STATUS_OPTIONS.map((option: { value: string; label: string }) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {canAdminister && (
              <Select value={selectedChannelId} onValueChange={setSelectedChannelId}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os canais" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os canais</SelectItem>
                  {(Array.isArray(channelsQuery.data) ? channelsQuery.data : []).map((channel: any) => (
                    <SelectItem key={channel.id} value={String(channel.id)}>
                      {channel.nomeFantasia || channel.name || `Canal ${channel.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="rounded-xl border border-dashed border-slate-200 px-4 py-3 text-sm text-slate-500">
              {canAdminister ? "A visão administrativa exibe toda a fila com status internos e agora permite segmentação por canal." : "A visão do canal exibe apenas as solicitações vinculadas ao seu canal ativo."}
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Protocolo</th>
                  {showAdminChannelColumn && <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Canal</th>}
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Cliente solicitado</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Atualização</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {listQuery.isLoading ? (
                  <tr>
                      <td colSpan={showAdminChannelColumn ? 6 : 5} className="px-4 py-10 text-center text-sm text-slate-500">

                      <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin" />
                      Carregando solicitações...
                    </td>
                  </tr>
                ) : requests.length === 0 ? (
                  <tr>
                      <td colSpan={showAdminChannelColumn ? 6 : 5} className="px-4 py-10 text-center text-sm text-slate-500">

                      Nenhuma solicitação encontrada com os filtros atuais.
                    </td>
                  </tr>
                ) : (
                  requests.map((request: any) => (
                    <tr key={request.id} className="hover:bg-slate-50/80">
                      <td className="px-4 py-4 text-sm font-medium text-slate-900">{request.protocol}</td>
                      {showAdminChannelColumn && <td className="px-4 py-4 text-sm text-slate-600">{request.channelNameSnapshot || "-"}</td>}
                      <td className="px-4 py-4 text-sm text-slate-700">
                        <div className="font-medium text-slate-900">{request.nomeFantasia}</div>
                        <div className="text-xs text-slate-500">{request.razaoSocial}</div>
                        <div className="text-xs text-slate-400">{request.cnpj}</div>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <div className="flex flex-col gap-2">
                          <Badge variant="outline" className="w-fit border-slate-300 bg-slate-50 text-slate-700">
                            {canAdminister ? request.adminStatusLabel : request.channelStatusLabel}
                          </Badge>
                          {canAdminister && (
                            <span className="text-xs text-slate-500">Canal vê como: {request.channelStatusLabel}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-500">
                        {request.updatedAt ? new Date(request.updatedAt).toLocaleString() : "-"}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <Button variant="outline" size="sm" onClick={() => handleOpenDetails(request.id)}>
                          Ver detalhes
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-h-[96vh] w-[96vw] max-w-[1180px] overflow-x-hidden overflow-y-auto rounded-[30px] border border-slate-200 bg-white p-0 shadow-2xl sm:w-[94vw] lg:w-[90vw]">
          <DialogHeader className="sticky top-0 z-10 border-b border-slate-100 bg-white/95 px-6 py-5 backdrop-blur sm:px-8 lg:px-10">
            <DialogTitle>Solicitação de novo cliente</DialogTitle>
            <DialogDescription>
              Preencha apenas os dados essenciais para iniciar a triagem. A operação complementa parametrização, integrações e validações administrativas nas próximas etapas.
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 py-6 pb-28 sm:px-8 lg:px-10 lg:pb-32">
            <div className="mx-auto max-w-6xl space-y-5">
              <Alert className="border-slate-200 bg-slate-50 text-slate-700 shadow-sm">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Formulário simplificado para o canal</AlertTitle>
                <AlertDescription>
                  Os campos obrigatórios liberam o envio. Informações complementares podem ser refinadas depois pela equipe administrativa durante a aprovação e parametrização.
                </AlertDescription>
              </Alert>

              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm lg:p-6">
                <div className="mb-4 flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-sm font-semibold text-slate-700 ring-1 ring-slate-200">1</div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Dados da empresa</h3>
                    <p className="text-sm text-slate-500">Identifique rapidamente a empresa que está entrando na esteira.</p>
                  </div>
                </div>
                <div className="grid gap-4 lg:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">CNPJ *</label>
                    <Input value={createForm.cnpj} placeholder="00.000.000/0000-00" onChange={(event) => setCreateForm((prev) => ({ ...prev, cnpj: maskCnpj(event.target.value) }))} />
                  </div>
                  <div className="space-y-2 lg:col-span-2">
                    <label className="text-sm font-medium text-slate-700">Razão social *</label>
                    <Input value={createForm.razaoSocial} placeholder="Empresa Exemplo LTDA" onChange={(event) => setCreateForm((prev) => ({ ...prev, razaoSocial: event.target.value }))} />
                  </div>
                  <div className="space-y-2 lg:col-span-3">
                    <label className="text-sm font-medium text-slate-700">Nome fantasia *</label>
                    <Input value={createForm.nomeFantasia} placeholder="Nome pelo qual a empresa é conhecida" onChange={(event) => setCreateForm((prev) => ({ ...prev, nomeFantasia: event.target.value }))} />
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm lg:p-6">
                <div className="mb-4 flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-sm font-semibold text-slate-700 ring-1 ring-slate-200">2</div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Ponto focal</h3>
                    <p className="text-sm text-slate-500">Defina quem será o contato principal da solicitação durante a triagem.</p>
                  </div>
                </div>
                <div className="grid gap-4 lg:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Nome completo *</label>
                    <Input value={createForm.responsibleName} placeholder="Ex.: Maria Silva" onChange={(event) => setCreateForm((prev) => ({ ...prev, responsibleName: event.target.value }))} />
                  </div>
                  <div className="space-y-2 lg:col-span-2">
                    <label className="text-sm font-medium text-slate-700">E-mail corporativo *</label>
                    <Input type="email" value={createForm.email} placeholder="maria@empresa.com" onChange={(event) => setCreateForm((prev) => ({ ...prev, email: event.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Telefone / WhatsApp *</label>
                    <Input value={createForm.phone} placeholder="(11) 99999-9999" onChange={(event) => setCreateForm((prev) => ({ ...prev, phone: maskPhone(event.target.value) }))} />
                  </div>
                  <div className="space-y-2 lg:col-span-2">
                    <label className="text-sm font-medium text-slate-700">Função do contato</label>
                    <Input value={createForm.contactRole} placeholder="Ex.: RH, Comercial, Financeiro" onChange={(event) => setCreateForm((prev) => ({ ...prev, contactRole: event.target.value }))} />
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm lg:p-6">
                <div className="mb-4 flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-sm font-semibold text-slate-700 ring-1 ring-slate-200">3</div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Dados do Vendedor</h3>
                    <p className="text-sm text-slate-500">Informe o responsável comercial do Canal para que ele também receba as comunicações da solicitação.</p>
                  </div>
                </div>
                <div className="grid gap-4 lg:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Nome completo *</label>
                    <Input value={createForm.sellerName} placeholder="Ex.: João Pereira" onChange={(event) => setCreateForm((prev) => ({ ...prev, sellerName: event.target.value }))} />
                  </div>
                  <div className="space-y-2 lg:col-span-2">
                    <label className="text-sm font-medium text-slate-700">E-mail do vendedor *</label>
                    <Input type="email" value={createForm.sellerEmail} placeholder="joao@canal.com.br" onChange={(event) => setCreateForm((prev) => ({ ...prev, sellerEmail: event.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Telefone / WhatsApp</label>
                    <Input value={createForm.sellerPhone} placeholder="(11) 99999-9999" onChange={(event) => setCreateForm((prev) => ({ ...prev, sellerPhone: maskPhone(event.target.value) }))} />
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm lg:p-6">
                <div className="mb-4 flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-sm font-semibold text-slate-700 ring-1 ring-slate-200">4</div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Detalhes do contrato</h3>
                    <p className="text-sm text-slate-500">Informe o básico para a equipe preparar análise comercial e implantação inicial.</p>
                  </div>
                </div>
                <div className="grid gap-4 lg:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Volume de vidas *</label>
                    <Input type="number" min={0} value={createForm.employeesCount} placeholder="Ex.: 1200" onChange={(event) => setCreateForm((prev) => ({ ...prev, employeesCount: event.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Início do contrato *</label>
                    <Input type="date" value={createForm.contractStartDate} onChange={(event) => setCreateForm((prev) => ({ ...prev, contractStartDate: event.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Encerramento do contrato *</label>
                    <Input type="date" value={createForm.contractEndDate} onChange={(event) => setCreateForm((prev) => ({ ...prev, contractEndDate: event.target.value }))} />
                  </div>
                </div>

                <div className="mt-5 space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900">Serviços solicitados *</h4>
                    <p className="text-xs text-slate-500">Selecione somente os serviços disponíveis para o contexto operacional atual, conforme os vínculos ativos do canal ou do cliente.</p>
                  </div>
                  <div className="grid gap-3 lg:grid-cols-2">
                    {serviceOptions.map((option) => (
                      <label key={option.value} className={`flex items-start gap-3 rounded-2xl border p-3 text-sm transition ${createForm.requestedServices.includes(option.value) ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}>
                        <Checkbox checked={createForm.requestedServices.includes(option.value)} onCheckedChange={(checked) => handleToggleService(option.value, Boolean(checked))} />
                        <span className="space-y-1">
                          <span className="block font-medium text-slate-900">{option.label}</span>
                          {option.description ? <span className="block text-xs text-slate-500">{option.description}</span> : null}
                          {option.category ? <span className="inline-flex rounded-full bg-white px-2.5 py-1 text-[11px] text-slate-500 ring-1 ring-slate-200">{option.category}</span> : null}
                        </span>
                      </label>
                    ))}
                    {serviceOptionsQuery.isLoading ? (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500 lg:col-span-2">
                        Carregando os serviços habilitados para o canal...
                      </div>
                    ) : null}
                    {!serviceOptionsQuery.isLoading && serviceOptions.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 lg:col-span-2">
                        Nenhum serviço está disponível para o contexto operacional atual. Revise os vínculos ativos do canal e, quando houver escopo de cliente, também os contratos vigentes antes de abrir a solicitação.
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm lg:p-6">
                <div className="mb-4 flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-sm font-semibold text-slate-700 ring-1 ring-slate-200">5</div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Contrato</h3>
                    <p className="text-sm text-slate-500">Registre o contexto comercial e, se desejar, a referência do arquivo que será compartilhado na triagem.</p>
                  </div>
                </div>
                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="space-y-2 lg:col-span-2">
                    <label className="text-sm font-medium text-slate-700">Anexo do contrato</label>
                    <Input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={(event) => setCreateForm((prev) => ({ ...prev, contractAttachmentName: event.target.files?.[0]?.name ?? "" }))} />
                    <p className="text-xs text-slate-500">Nesta etapa, a solicitação registra a referência do arquivo para orientar a triagem administrativa.</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Contexto contratual</label>
                    <Textarea value={createForm.contractContext} rows={4} placeholder="Ex.: contratação nova, migração de carteira, período de implantação, observações comerciais relevantes." onChange={(event) => setCreateForm((prev) => ({ ...prev, contractContext: event.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Observações adicionais</label>
                    <Textarea value={createForm.notes} rows={4} placeholder="Inclua aqui qualquer observação útil para a avaliação inicial da operação." onChange={(event) => setCreateForm((prev) => ({ ...prev, notes: event.target.value }))} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="sticky bottom-0 z-20 border-t border-slate-200 bg-white px-6 py-4 shadow-[0_-12px_32px_rgba(15,23,42,0.08)] sm:px-8 lg:px-10">
            <div className="mx-auto flex w-full max-w-6xl justify-end">
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-end">
                <Button variant="outline" className="h-11 min-w-[170px]" onClick={() => setCreateForm(createEmptyForm())}>Limpar formulário</Button>
                <Button className="h-11 min-w-[190px]" onClick={handleSubmitCreate} disabled={!isCreateFormValid || createMutation.isPending}>
                  {createMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ClipboardList className="mr-2 h-4 w-4" />}
                  Enviar cadastro
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={detailDialogOpen} onOpenChange={(open) => {
        setDetailDialogOpen(open);
        if (!open) {
          setSelectedRequestId(null);
          setPendingResponse("");
          setActivationSummary("");
          setRejectionReason("");
          setChecklistNote("");
          setChecklist(createEmptyChecklist());
        }
      }}>
        <DialogContent className="left-0 top-0 flex h-screen w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 bg-slate-50 p-0 md:left-[5.5rem] md:w-[calc(100vw-5.5rem)] md:max-w-[calc(100vw-5.5rem)] lg:left-64 lg:w-[calc(100vw-16rem)] lg:max-w-[calc(100vw-16rem)]">
          <DialogHeader className="border-b border-slate-200 bg-white px-6 py-5 shadow-sm sm:px-8">
            <DialogTitle>Detalhes da solicitação</DialogTitle>
            <DialogDescription>
              Acompanhe o histórico, as pendências e as ações disponíveis para a solicitação selecionada.
            </DialogDescription>
          </DialogHeader>

          {detailQuery.isLoading || !selectedRequest ? (
            <div className="flex min-h-[calc(100vh-88px)] items-center justify-center px-6 py-16 text-sm text-slate-500 sm:px-8">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Carregando detalhes da solicitação...
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6 sm:px-8">
              <div className="mx-auto grid max-w-[1600px] gap-6 xl:grid-cols-[1.2fr_0.8fr]">
                <div className="space-y-6">
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Protocolo</p>
                      <p className="mt-2 text-lg font-semibold text-slate-900">{selectedRequest.protocol}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Status Canal</p>
                      <p className="mt-2 text-sm font-semibold text-slate-900">{selectedRequest.channelStatusLabel}</p>
                    </div>
                  </div>

                  {selectedRequest.currentPendingTitle && (
                    <Alert className="border-amber-200 bg-amber-50 text-amber-900">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>{selectedRequest.currentPendingTitle}</AlertTitle>
                      <AlertDescription>{selectedRequest.currentPendingMessage}</AlertDescription>
                    </Alert>
                  )}

                  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Dados da solicitação</h3>
                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                      <div>
                        <p className="text-xs text-slate-500">Cliente</p>
                        <p className="font-medium text-slate-900">{selectedRequest.nomeFantasia}</p>
                        <p className="text-sm text-slate-500">{selectedRequest.razaoSocial}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">CNPJ</p>
                        <p className="font-medium text-slate-900">{selectedRequest.cnpj}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Ponto focal</p>
                        <p className="font-medium text-slate-900">{selectedRequest.responsibleName || requestPayload.responsibleName || "Não informado"}</p>
                        <p className="text-sm text-slate-500">{selectedRequest.contactRole || requestPayload.contactRole || "Função não informada"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Contato</p>
                        <p className="font-medium text-slate-900">{selectedRequest.email || requestPayload.email || "E-mail não informado"}</p>
                        <p className="text-sm text-slate-500">{selectedRequest.phone || requestPayload.phone || "Telefone não informado"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Vendedor</p>
                        <p className="font-medium text-slate-900">{selectedRequest.sellerName || requestPayload.sellerName || "Não informado"}</p>
                        <p className="text-sm text-slate-500">{selectedRequest.sellerEmail || requestPayload.sellerEmail || "E-mail não informado"}</p>
                        <p className="text-sm text-slate-500">{selectedRequest.sellerPhone || requestPayload.sellerPhone || "Telefone não informado"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Volume de vidas</p>
                        <p className="font-medium text-slate-900">{requestPayload.employeesCount ?? "Não informado"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Início do contrato</p>
                        <p className="font-medium text-slate-900">{resolvedContractStartDate || "Não informado"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Encerramento do contrato</p>
                        <p className="font-medium text-slate-900">{resolvedContractEndDate || "Não informado"}</p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-xs text-slate-500">Serviços solicitados</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {requestedServiceDetails.length > 0 ? (
                            requestedServiceDetails.map((service: any) => (
                              <Badge key={service.key || service.label} variant="outline">{service.label}</Badge>
                            ))
                          ) : (selectedRequest.requestedServices ?? []).length > 0 ? (
                            (selectedRequest.requestedServices ?? []).map((service: string) => (
                              <Badge key={service} variant="outline">{service}</Badge>
                            ))
                          ) : (
                            <span className="text-sm text-slate-500">Nenhum serviço informado.</span>
                          )}
                        </div>
                      </div>
                      {requestedServiceDetails.length > 0 ? (
                        <div className="grid gap-3 md:col-span-2 lg:grid-cols-2">
                          {requestedServiceDetails.map((service: any) => (
                            <div key={`detail-${service.key || service.label}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                              <p className="font-medium text-slate-900">{service.label}</p>
                              <p className="mt-1 text-sm text-slate-500">{service.description || "Sem descrição operacional cadastrada."}</p>
                              {service.category ? <p className="mt-2 text-xs uppercase tracking-[0.12em] text-slate-500">{service.category}</p> : null}
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {requestPayload.contractAttachmentName ? (
                        <div>
                          <p className="text-xs text-slate-500">Arquivo de contrato informado</p>
                          <p className="font-medium text-slate-900">{requestPayload.contractAttachmentName}</p>
                        </div>
                      ) : null}
                      {selectedRequest.contractContext ? (
                        <div className="md:col-span-2">
                          <p className="text-xs text-slate-500">Contexto contratual</p>
                          <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{selectedRequest.contractContext}</p>
                        </div>
                      ) : null}
                      {selectedRequest.notes ? (
                        <div className="md:col-span-2">
                          <p className="text-xs text-slate-500">Observações do Canal</p>
                          <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{selectedRequest.notes}</p>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {permissions?.canRespondComplement && (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
                      <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-amber-700">Responder pendência</h3>
                      <Textarea className="mt-4 bg-white" rows={4} value={pendingResponse} onChange={(event) => setPendingResponse(event.target.value)} placeholder="Descreva como a pendência foi tratada ou quais dados complementares estão sendo enviados." />
                      <div className="mt-4 flex justify-end">
                        <Button onClick={() => respondComplementMutation.mutate({ id: selectedRequest.id, responseMessage: pendingResponse })} disabled={respondComplementMutation.isPending || pendingResponse.trim().length < 5}>
                          {respondComplementMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                          Enviar resposta
                        </Button>
                      </div>
                    </div>
                  )}

                  {canAdminister && (
                    <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Operação administrativa</h3>
                          <p className="mt-1 text-sm text-slate-500">Aprove, solicite complementos, parametrize e ative o cliente quando a implantação estiver concluída, sem depender de integração externa nesta fase 1.</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {permissions?.canApprove && (
                            <Button onClick={() => approveMutation.mutate({ id: selectedRequest.id })} disabled={approveMutation.isPending}>
                              {approveMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                              Aprovar para implantação
                            </Button>
                          )}
                          {permissions?.canRequestComplement && (
                            <Button variant="outline" onClick={() => setPendingDialogOpen(true)}>
                              Solicitar complemento
                            </Button>
                          )}
                          {permissions?.canStartParameterization && (
                            <Button variant="outline" onClick={() => startParameterizationMutation.mutate({ id: selectedRequest.id })} disabled={startParameterizationMutation.isPending}>
                              {startParameterizationMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                              Iniciar parametrização
                            </Button>
                          )}
                        </div>
                      </div>

                      {showImplementationPanel && (
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <h4 className="text-sm font-semibold text-slate-900">Checklist de ativação</h4>
                              <p className="text-xs text-slate-500">Somente solicitações com checklist completo podem ser encerradas como aptas para operar.</p>
                            </div>
                            <Button variant="ghost" onClick={syncChecklistFromRequest}>Recarregar do servidor</Button>
                          </div>

                          {isTerminalRequest && (
                            <Alert className="mt-4 border-slate-300 bg-white text-slate-900">
                              <AlertCircle className="h-4 w-4" />
                              <AlertTitle>Solicitação encerrada</AlertTitle>
                              <AlertDescription>
                                Esta solicitação está disponível apenas para consulta. Novas interações operacionais não são mais permitidas após o encerramento.
                              </AlertDescription>
                            </Alert>
                          )}

                          <div className="mt-4 grid gap-3 md:grid-cols-2">
                            {[
                              ["clientConfigured", "Cadastro do cliente estruturado"],
                              ["contractConfigured", "Contrato parametrizado"],
                              ["servicesConfigured", "Serviços liberados"],
                              ["integrationsConfigured", "Integrações configuradas"],
                              ["finalValidationCompleted", "Validação final concluída"],
                            ].map(([key, label]) => (
                              <label key={key} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700">
                                <Checkbox
                                  checked={Boolean(checklist[key as keyof ClientRegistrationActivationChecklist])}
                                  onCheckedChange={(checked) => handleChecklistChange(key as keyof ClientRegistrationActivationChecklist, Boolean(checked))}
                                  disabled={!canEditImplementation}
                                />
                                <span>{label}</span>
                              </label>
                            ))}
                          </div>

                          <div className="mt-4 space-y-2">
                            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Notas internas da implantação</p>
                            <p className="text-xs text-slate-500">Conteúdo exclusivamente administrativo. Este texto não é exibido ao Canal.</p>
                            <Textarea
                              className="bg-white"
                              rows={3}
                              value={checklistNote}
                              onChange={(event) => setChecklistNote(event.target.value)}
                              placeholder="Registre decisões, observações e pendências internas da implantação."
                              disabled={!canEditImplementation}
                            />
                          </div>

                          <div className="mt-4 space-y-2">
                            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Resumo final ao Canal</p>
                            <p className="text-xs text-slate-500">Esta mensagem será exibida ao Canal no encerramento positivo da solicitação, quando o status for atualizado para Apto para operar.</p>
                            <Textarea
                              className="bg-white"
                              rows={3}
                              value={activationSummary}
                              onChange={(event) => setActivationSummary(event.target.value)}
                              placeholder="Descreva o que foi concluído e quais orientações finais o Canal deve considerar após o encerramento da solicitação."
                              disabled={!canEditImplementation && !permissions?.canActivate}
                            />
                          </div>

                          <div className="mt-4 flex flex-wrap justify-end gap-2">
                            <Button
                              variant="outline"
                              onClick={() =>
                                saveChecklistMutation.mutate({
                                  id: selectedRequest.id,
                                  checklist,
                                  note: checklistNote.trim() ? checklistNote : null,
                                  finalSummary: activationSummary.trim() ? activationSummary : null,
                                })
                              }
                              disabled={saveChecklistMutation.isPending || !canEditImplementation}
                            >
                              {saveChecklistMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                              Salvar checklist
                            </Button>
                            {permissions?.canActivate && (
                              <Button onClick={() => activateMutation.mutate({ id: selectedRequest.id, summary: activationSummary.trim() || undefined })} disabled={activateMutation.isPending}>
                                {activateMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                                Encerrar solicitação
                              </Button>
                            )}
                          </div>
                        </div>
                      )}

                      {canRejectRequest && (
                        <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                          <h4 className="text-sm font-semibold text-red-900">Reprovar solicitação</h4>
                          <p className="mt-1 text-xs text-red-700">Use essa ação somente quando a solicitação não puder seguir para formalização.</p>
                          <Textarea className="mt-4 bg-white" rows={3} value={rejectionReason} onChange={(event) => setRejectionReason(event.target.value)} placeholder="Explique ao Canal o motivo da reprovação." />
                          <div className="mt-4 flex justify-end">
                            <Button variant="destructive" onClick={() => rejectMutation.mutate({ id: selectedRequest.id, reason: rejectionReason })} disabled={rejectMutation.isPending || rejectionReason.trim().length < 5}>
                              {rejectMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                              Reprovar solicitação
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-6">
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Histórico da solicitação</h3>
                    <div className="mt-4 space-y-3">
                      {events.length === 0 ? (
                        <p className="text-sm text-slate-500">Nenhum evento registrado até o momento.</p>
                      ) : (
                        events.map((event: any) => (
                          <div key={event.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-sm font-semibold text-slate-900">{event.title}</p>
                              <Badge variant="outline">{event.visibility === "INTERNAL" ? "Interno" : "Canal"}</Badge>
                            </div>
                            {event.description ? <p className="mt-2 text-sm text-slate-600">{event.description}</p> : null}
                            <div className="mt-3 text-xs text-slate-500">
                              {event.actorNameSnapshot || "Sistema"} · {event.createdAt ? new Date(event.createdAt).toLocaleString() : "-"}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={pendingDialogOpen} onOpenChange={setPendingDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Solicitar complemento ao Canal</DialogTitle>
            <DialogDescription>
              A pendência ficará visível para o Canal até que ele responda e retorne a solicitação para análise.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Título da pendência</label>
              <Input value={pendingTitle} onChange={(event) => setPendingTitle(event.target.value)} placeholder="Ex.: Enviar contrato social atualizado" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Mensagem ao Canal</label>
              <Textarea value={pendingMessage} onChange={(event) => setPendingMessage(event.target.value)} rows={5} placeholder="Detalhe o que precisa ser ajustado, anexado ou confirmado antes da aprovação." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDialogOpen(false)}>Cancelar</Button>
            <Button onClick={() => requestComplementMutation.mutate({ id: selectedRequestId ?? 0, title: pendingTitle, message: pendingMessage })} disabled={requestComplementMutation.isPending || pendingTitle.trim().length < 3 || pendingMessage.trim().length < 5}>
              {requestComplementMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Registrar pendência
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SalviaLayout>
  );
}
